// Hook React que conecta deviceId + sessionManager + pageTracker + trackingQueue.
// Se monta UNA SOLA VEZ a nivel app (ver App.js), no en cada página.
//
// Responsabilidades:
// - Inicializa device + sesión al montar (Fase 1, ya existente).
// - Arranca el flush periódico de la cola de page views (Fase 2).
// - Escucha cambios de pathname (React Router useLocation) y registra page views.
// - Al cerrar la tab (pagehide), cierra el page view actual y la sesión via Beacon API.

import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { initTrackingSession } from "./sessionManager";
import { trackPageview, startPageTracker, finalizePageTracking } from "./pageTracker";
import { startQueueFlusher } from "./trackingQueue";

/**
 * @param {string|null} token - token de auth actual, para linkear el device al user si está logueado.
 */
export default function useTracking(token = null) {
  const location = useLocation();
  const identityRef = useRef({ deviceId: null, sessionUuid: null });

  // Inicialización: una sola vez al montar.
  useEffect(() => {
    let cancelled = false;

    startQueueFlusher();
    startPageTracker();

    initTrackingSession(token).then(({ deviceId, sessionUuid }) => {
      if (!cancelled) {
        identityRef.current = { deviceId, sessionUuid };
        // Registra el page view inicial (la ruta con la que arrancó la app).
        trackPageview(location.pathname, { deviceId, sessionUuid });
      }
    });

    const handlePageHide = () => {
      finalizePageTracking(identityRef.current);
    };
    window.addEventListener("pagehide", handlePageHide);

    return () => {
      cancelled = true;
      window.removeEventListener("pagehide", handlePageHide);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Cambios de ruta: registra un nuevo page view por cada pathname distinto.
  useEffect(() => {
    const { deviceId, sessionUuid } = identityRef.current;
    if (!deviceId || !sessionUuid) return;
    trackPageview(location.pathname, { deviceId, sessionUuid });
  }, [location.pathname]);
}
